import type { CalendarDate } from "@internationalized/date";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useContext, type FC, type KeyboardEvent, type ReactNode } from "react";
import { useDateFormatter } from "react-aria";
import {
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarStateContext,
  DateField,
  DateInput,
  DateSegment,
  Heading,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Button, ButtonVariant } from "../Buttons/button.js";
import { Size } from "../helpers/helpers.js";
import { inputStyle } from "../Inputs/input.js";

/**
 * @file
 * Presentation shared by every date picker in the product. Compose these
 * inside a react-aria `<Calendar>` / `<RangeCalendar>` rather than
 * re-declaring the grid and cell styling per picker, so the pickers can't
 * drift apart visually.
 */

const headingClass = "text-center text-sm font-medium";

export interface CalendarNavigationHeaderProps {
  /**
   * `short` abbreviates the month name, so the title takes the same width in
   * every month and a one-month calendar doesn't resize as it is paged.
   * Single-month calendars only.
   */
  monthFormat?: "long" | "short";
}

/**
 * Month title flanked by the calendar's previous/next page buttons. Must be
 * rendered inside a `<Calendar>` / `<RangeCalendar>`, which owns the slots
 * the buttons bind to.
 */
export const CalendarNavigationHeader: FC<CalendarNavigationHeaderProps> = ({
  monthFormat = "long",
}) => (
  <header className="mb-4 grid grid-cols-[auto_1fr_auto] items-center gap-4">
    <Button slot="previous" size={Size.SMALL} variant={ButtonVariant.Tertiary}>
      <IconChevronLeft className="size-5 shrink-0 text-gray-400" />
    </Button>
    {monthFormat === "short" ? (
      <AbbreviatedMonthHeading />
    ) : (
      <Heading className={headingClass} />
    )}
    <Button slot="next" size={Size.SMALL} variant={ButtonVariant.Tertiary}>
      <IconChevronRight className="size-5 shrink-0 text-gray-400" />
    </Button>
  </header>
);

/**
 * The visible month, abbreviated. Replaces the title the calendar puts on
 * `Heading` through context, which is always the full month name.
 */
const AbbreviatedMonthHeading: FC = () => {
  const state = useContext(CalendarStateContext);
  const formatter = useDateFormatter({
    month: "short",
    year: "numeric",
    timeZone: state?.timeZone,
  });

  return (
    <Heading className={headingClass}>
      {state
        ? formatter.format(state.visibleRange.start.toDate(state.timeZone))
        : null}
    </Heading>
  );
};

const calendarCellClass =
  "my-[1px] size-7 rounded-full text-center align-middle leading-7 outline-hidden hover:bg-gray-100 disabled:text-gray-300 data-[focus-visible]:ring-2 data-[focus-visible]:ring-primary data-[outside-month]:invisible data-[today]:bg-indigo-200 selected:bg-primary selected:font-medium selected:text-primary-foreground";

export interface CalendarMonthProps {
  /**
   * Months past the calendar's first visible month. Omit on a single-month
   * calendar; a two-month calendar renders one grid per offset (0 and 1).
   */
  offsetMonths?: number;
  /**
   * `range` runs a selection together into a single pill with rounded ends;
   * `single` keeps each selected day round on its own.
   */
  selectionMode: "single" | "range";
}

/** One month's grid of days. */
export const CalendarMonth: FC<CalendarMonthProps> = ({
  offsetMonths,
  selectionMode,
}) => (
  <CalendarGrid
    className="text-sm"
    offset={offsetMonths === undefined ? undefined : { months: offsetMonths }}
  >
    <CalendarGridHeader>
      {(day) => (
        <CalendarHeaderCell className="size-7 text-center text-xs leading-7 font-medium text-muted-foreground">
          {day}
        </CalendarHeaderCell>
      )}
    </CalendarGridHeader>
    <CalendarGridBody>
      {(date) => (
        <CalendarCell
          className={twMerge(
            calendarCellClass,
            selectionMode === "single"
              ? "selected:rounded-full"
              : "selected:rounded-none selected:data-[selection-end]:rounded-r-full selected:data-[selection-start]:rounded-l-full",
          )}
          date={date}
        />
      )}
    </CalendarGridBody>
  </CalendarGrid>
);

/** Bordered box giving typed date entry the look of a text input. */
export const CalendarDateInputBox: FC<{
  children: ReactNode;
  className?: string;
}> = ({ children, className }) => (
  <div
    className={inputStyle().input({
      className: twMerge(
        "flex items-center gap-1 focus-within:ring-2 focus-within:ring-indigo-600 focus-within:ring-inset",
        className,
      ),
    })}
  >
    {children}
  </div>
);

export interface CalendarDateInputProps {
  ariaLabel: string;
  value: CalendarDate | null;
  isRequired?: boolean;
  maxValue?: CalendarDate;
  onChange: (value: CalendarDate) => void;
  onEnter?: () => void;
}

/** Typed (segmented) entry for a single date, paired with a calendar the caller keeps in sync. */
export const CalendarDateInput: FC<CalendarDateInputProps> = ({
  ariaLabel,
  value,
  isRequired,
  maxValue,
  onChange,
  onEnter,
}) => {
  const handleKeyDown = (event: KeyboardEvent) => {
    if (onEnter && event.key === "Enter") {
      event.preventDefault();
      onEnter();
    }
  };

  return (
    <DateField
      aria-label={ariaLabel}
      value={value}
      isRequired={isRequired}
      maxValue={maxValue}
      onChange={(newValue) => {
        // Clearing a segment leaves an incomplete date; keep the last
        // complete one rather than pushing null through the picker.
        if (newValue) {
          onChange(newValue);
        }
      }}
      onKeyDown={handleKeyDown}
    >
      <DateInput>
        {(segment) => (
          <DateSegment
            className="-mx-0.5 rounded-md px-1 py-0.5 focus:bg-primary focus:text-primary-foreground data-[placeholder]:text-muted-foreground"
            segment={segment}
          />
        )}
      </DateInput>
    </DateField>
  );
};

export interface CalendarDateRangeInputProps {
  /**
   * Typed counterpart of the calendar's selection. A `null` bound renders
   * empty segments, for pickers whose range is unset (e.g. while a relative
   * preset is active).
   */
  value: { start: CalendarDate | null; end: CalendarDate | null };
  isRequired?: boolean;
  /** Latest selectable day, applied to both bounds. */
  maxValue?: CalendarDate;
  onStartChange: (value: CalendarDate) => void;
  onEndChange: (value: CalendarDate) => void;
  /**
   * Applies the typed range on Enter. Omit to let Enter fall through to the
   * surrounding dialog or form.
   */
  onEnter?: () => void;
}

/**
 * Typed (segmented) entry for the two bounds of a range, paired with a
 * `<RangeCalendar>` the caller keeps in sync.
 */
export const CalendarDateRangeInput: FC<CalendarDateRangeInputProps> = ({
  value,
  isRequired,
  maxValue,
  onStartChange,
  onEndChange,
  onEnter,
}) => (
  <CalendarDateInputBox className="max-w-64">
    <CalendarDateInput
      ariaLabel="From date"
      value={value.start}
      isRequired={isRequired}
      maxValue={maxValue}
      onChange={onStartChange}
      onEnter={onEnter}
    />
    <div className="flex-none">–</div>
    <CalendarDateInput
      ariaLabel="To date"
      value={value.end}
      isRequired={isRequired}
      maxValue={maxValue}
      onChange={onEndChange}
      onEnter={onEnter}
    />
  </CalendarDateInputBox>
);
